<template>
  <div
    class="w-full"
    v-bind="$attrs"
  >
    <span
      v-if="selected"
      class="absolute inset-y-0 left-0 w-1 bg-[var(--ocv-secondary-500)] rounded-tr-lg rounded-br-lg"
      aria-hidden="true"
    />
    <template v-if="isNaN(Number(item.key))">
      <NuxtLink
        :data-menu-item="item.label.toLowerCase()"
        :class="{ 'text-[var(--ocv-secondary-500)] dark:text-[var(--ocv-secondary-500)]': props.selected }"
        class="inline-flex items-center w-full text-sm font-semibold transition-colors duration-150 cursor-pointer hover:text-[var(--ocv-secondary-500)] dark:text-gray-100 dark:hover:text-[var(--ocv-secondary-500)]"
        :to="item.href"
        @click="onItemClick()"
      >
        <component
          :is="'i-' + item.icon"
          v-if="item.icon != undefined || item.icon != null"
          class="w-6 h-6"
        />
        <div
          v-else
          class="w-6 h-6"
        />
        <span
          v-if="item.label != undefined"
          class="ml-4"
          v-text="item.label"
        />
        <button
          v-if="item.items && item.items.length > 0"
          class="inline-flex justify-between items-center ml-auto w-full text-sm font-semibold transition-colors duration-150 max-w-fit hover:text-[var(--ocv-secondary-500)] dark:hover:text-[var(--ocv-secondary-500)]"
          aria-haspopup="true"
        >
          <svg
            v-if="isActive"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            class="w-4 h-4"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="m19.5 8.25-7.5 7.5-7.5-7.5"
            />
          </svg>
          <svg
            v-else
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            class="w-4 h-4"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="m8.25 4.5 7.5 7.5-7.5 7.5"
            />
          </svg>
        </button>
      </NuxtLink>
    </template>
    <template v-else>
      <NuxtLink
        :data-menu-item="item.label.toLowerCase()"
        :class="{ 'text-[var(--ocv-secondary-500)] dark:text-[var(--ocv-secondary-500)]': props.selected }"
        class="inline-flex items-center w-full text-sm font-semibold transition-colors duration-150 cursor-pointer hover:text-[var(--ocv-secondary-500)] dark:text-gray-100 dark:hover:text-[var(--ocv-secondary-500)]"
        :to="item.href"
        @click="onItemClick()"
      >
        <component
          :is="'i-' + item.icon"
          v-if="item.icon != undefined || item.icon != null"
          class="w-6 h-6"
        />
        <div
          v-else
          class="w-6 h-6"
        />
        <span
          v-if="item.label != undefined"
          class="ml-4"
          v-text="item.label"
        />
        <button
          v-if="item.items && item.items.length > 0"
          class="inline-flex justify-between items-center ml-auto w-full text-sm font-semibold transition-colors duration-150 max-w-fit hover:text-[var(--ocv-secondary-500)] dark:hover:text-[var(--ocv-secondary-500)]"
          aria-haspopup="true"
        >
          <svg
            v-if="isActive"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            class="w-4 h-4"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="m19.5 8.25-7.5 7.5-7.5-7.5"
            />
          </svg>
          <svg
            v-else
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            class="w-4 h-4"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="m8.25 4.5 7.5 7.5-7.5 7.5"
            />
          </svg>
        </button>
      </NuxtLink>
    </template>
  </div>
</template>

<script setup lang="ts">
import type { MenuItem } from "primevue/menuitem";

const emit = defineEmits(["update:selected"]);
const props = defineProps<{
  item: MenuItem;
  isActive: boolean | undefined;
  selected: boolean;
}>();
defineOptions({
  inheritAttrs: false,
});
const { setNavigationContext } = useNavigationContext();

const route = useRoute();

const { $faro } = useNuxtApp();

const criticalRoutes = new Set(["/city/invoices", "/city/settings"]);

function toUserActionName(destination: string) {
  const normalizedPath = destination.split("?")[0].split("#")[0];
  const slug = normalizedPath
    .replace(/^\/+/, "")
    .replace(/\//g, "_")
    .replace(/[^a-zA-Z0-9_]/g, "")
    .toLowerCase();

  return `sidebar_navigate_${slug || "home"}`;
}

function onItemClick() {
  // Set navigation context for breadcrumb navigation
  if (props.item?.label && props.item?.href) {
    setNavigationContext({
      route: route.fullPath,
      displayName: props.item.label,
      data: { fromSidebar: true },
    });

    const destination = String(props.item.href);
    const actionName = toUserActionName(destination);

    $faro.api.startUserAction(
      actionName,
      {
        menuLabel: String(props.item.label),
        menuKey: String(props.item.key),
        menuHref: destination,
        routeFrom: route.fullPath,
        routeTo: destination,
        hasChildren: String(Boolean(props.item.items?.length)),
      },
      {
        triggerName: "sidebarMenuClick",
        importance: criticalRoutes.has(destination) ? "critical" : "normal",
      },
    );
  }

  emit("update:selected", { href: props.item.href, key: props.item.key });
  if (props.item?.action) {
    props.item?.action();
  }
}
</script>
